import {
  AuthProvider,
  NetworkProvider as ToolkitNetworkProvider,
} from '@locus-taxy/encore-ui-network';
import { GenericErrorPage } from '@locus-taxy/encore-ui-toolkit';
import React from 'react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useIdentity } from '../../auth';
import { registerEndpoints } from './endpoints';
import { getApiBaseUrl } from '../../config';
import { useNetworkAuthReadiness } from './auth';
import LoadingSpinner from '../../components/LoadingSpinner';
import LoginPage from '../../components/LoginPage';
import { useUser } from '../../hooks/useUser';
import InterceptorInstaller from './InterceptorInstaller';
import { useClientId } from '../../hooks/useClientId';

interface NetworkProviderProps {
  children: React.ReactNode;
  baseUrl?: string;
}

const LoadingSpinnerComponent: React.FC = () => (
  <div className="flex h-screen items-center justify-center">
    <LoadingSpinner showRandomMessage={false} />
  </div>
);

export const NetworkProvider: React.FC<NetworkProviderProps> = ({
  children,
  baseUrl: baseUrlProp,
}) => {
  const { t } = useTranslation();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessToken,
    logout,
  } = useIdentity();
  const { isNetworkAuthReady, hasAuthFailed } = useNetworkAuthReadiness({
    isAuthenticated,
    getAccessToken,
  });

  // A failed token fetch means the session is gone (e.g. the Auth0 refresh
  // token expired/was revoked) — there's nothing to retry against, so ask
  // the user to log in again rather than offering a Retry that can never
  // succeed. logout() clears the stale @@auth0spajs@@ entries and redirects.
  const handleLoginAgain = React.useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } }).catch(
      error => {
        // eslint-disable-next-line no-console
        console.error(
          '[NetworkProvider] Failed to log out after auth failure:',
          error
        );
      }
    );
  }, [logout]);
  const { user } = useUser();
  const clientId = useClientId();
  // Construct dynamic base URL using clientId
  // Use prop if provided (for backward compatibility), otherwise construct dynamically
  const baseUrl = React.useMemo(() => {
    if (baseUrlProp) {
      return baseUrlProp;
    }
    try {
      // getApiBaseUrl handles the case where clientId is missing by providing a fallback
      return getApiBaseUrl(clientId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[NetworkProvider] Failed to construct API base URL:',
        error instanceof Error ? error.message : error
      );
      // Return null as safe fallback - component will handle this case
      return null;
    }
  }, [baseUrlProp, clientId]);

  const onBehalfOf = React.useMemo(() => {
    const userId = user?.userId;
    if (!userId) {
      return 'anonymous';
    }
    const organizationId = user?.homeTenant;
    if (!organizationId) {
      return 'anonymous';
    }
    return `${organizationId}/${userId}`;
  }, [user]);

  // Register endpoints synchronously
  registerEndpoints();

  // In WebView mode isAuthLoading stays true until native provides a
  // session, so this also covers "no session yet" without any
  // WebView-specific branching here.
  if (isAuthLoading) {
    return <LoadingSpinnerComponent />;
  }

  // Every check below only applies once authenticated (an unauthenticated
  // user falls straight through to the baseUrl/LoginPage handling further
  // down) — nesting under one `isAuthenticated` check instead of repeating
  // `isAuthenticated &&` on each branch keeps this function's cyclomatic
  // complexity down.
  if (isAuthenticated) {
    if (hasAuthFailed) {
      // The token fetch failed: the session itself is gone, so ask the
      // user to log in again instead of offering a Retry that can't work.
      return (
        <GenericErrorPage
          title={t(
            'dashboard.networkProvider.authErrorTitle',
            'Session Expired'
          )}
          message={t(
            'dashboard.networkProvider.authErrorMessage',
            'Your session has expired. Please log in again.'
          )}
          showRetry={true}
          onRetry={handleLoginAgain}
          retryButtonText={t(
            'dashboard.networkProvider.loginAgainButton',
            'Log In'
          )}
        />
      );
    }

    // Don't render until auth is ready.
    if (!isNetworkAuthReady) {
      return <LoadingSpinnerComponent />;
    }

    // Don't render until we have a clientId.
    if (!clientId) {
      return <LoadingSpinnerComponent />;
    }
  }

  // Handle case where baseUrl construction failed (missing apiBaseDomain config)
  if (!baseUrl) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-background">
        <div className="p-6 text-center text-destructive">
          <div className="mb-2 text-xl font-bold">
            {t(
              'dashboard.networkProvider.configErrorTitle',
              'Configuration Error'
            )}
          </div>
          <div>
            {t(
              'dashboard.networkProvider.missingBaseUrl',
              'Missing API base URL configuration. Please check your environment configuration file.'
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ToolkitNetworkProvider>
      <AuthProvider baseUrl={baseUrl}>
        <InterceptorInstaller
          baseUrl={baseUrl}
          getAccessTokenSilently={getAccessToken}
          onBehalfOf={onBehalfOf}
        />
        {isAuthenticated ? children : <LoginPage />}
      </AuthProvider>
    </ToolkitNetworkProvider>
  );
};
